import axios from 'axios';

// ── Base URL resolution ────────────────────────────────────────────────────
// Priority:
//   1. REACT_APP_API_URL env var  →  always wins (set this in Render / CI)
//   2. localhost / 127.0.0.1     →  local dev fallback
//
// The old "same-hostname:5000" fallback was the root cause of 404s in
// production: the frontend is served from www.docenlinea.com.co but the
// backend lives on a different Render service, so constructing
// http://www.docenlinea.com.co:5000/api never reaches the backend.
//
// ⚠️  Make sure REACT_APP_API_URL is set in the Render build environment
//     for the frontend service, e.g.:
//       REACT_APP_API_URL=https://docenlinea-backend.onrender.com/api
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // Local development only
  return 'http://localhost:5000/api';
};

const API_URL = getApiUrl();

console.log('🔗 API URL:', API_URL);

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

// ── Request interceptor: attach JWT ───────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Renovación de sesión ───────────────────────────────────────────────────
//
// El access token dura 15 minutos. Cuando caduca el backend responde 401, y aquí
// se canjea el refresh token por un par nuevo, se reintenta la petición original
// y el usuario no se entera. Solo si el refresh también falla se cierra sesión.
//
// El refresh se ROTA en cada uso, así que solo puede haber UNA renovación en
// vuelo: si tres peticiones fallan a la vez con 401 y cada una pidiera la suya,
// la segunda presentaría un token ya rotado y el backend lo leería como robo
// (revocaría la familia y echaría al usuario). De ahí el promise compartido.
let renovacionEnVuelo = null;

// No borra los borradores de historia clínica (hc-draft:*, utils/historiaDraft):
// esto corre cuando la sesión caduca sin remedio, a menudo con el médico a media
// escritura, y el borrador es lo que le permite recuperar la historia al volver
// a entrar. Se borran solo en el cierre de sesión explícito (AuthContext.logout).
const limpiarSesion = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('whatsappScoped');
};

const irALogin = () => {
  limpiarSesion();
  // Evita el bucle si ya estamos en /login. El optional chaining no es adorno:
  // window.location puede venir sustituido (tests, entornos embebidos) sin
  // pathname, y una excepción aquí enmascararía el error real de la petición.
  if (!window.location?.pathname?.startsWith('/login')) {
    window.location.href = '/login';
  }
};

const renovarSesion = () => {
  if (renovacionEnVuelo) return renovacionEnVuelo;

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return Promise.reject(new Error('sin refresh token'));

  // axios.post directo y no `api`: con la instancia, un 401 aquí volvería a
  // entrar por este mismo interceptor.
  renovacionEnVuelo = axios
    .post(`${API_URL}/auth/refresh`, { refreshToken })
    .then(({ data }) => {
      const nuevo = data?.data;
      if (!nuevo?.token) throw new Error('respuesta de refresco invalida');
      localStorage.setItem('token', nuevo.token);
      if (nuevo.refreshToken) localStorage.setItem('refreshToken', nuevo.refreshToken);
      return nuevo.token;
    })
    .finally(() => { renovacionEnVuelo = null; });

  return renovacionEnVuelo;
};

// ── Response interceptor: renovación + manejo unificado de errores ─────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error('❌ API Error:', error.message);

    if (error.code === 'ERR_NETWORK') {
      console.error('🚫 No se puede conectar al backend en:', API_URL);
    }

    const original = error.config;
    const es401 = error.response?.status === 401;

    // Casos en los que NO se intenta renovar:
    //  - no es un 401
    //  - la peticion ya se reintento una vez (evita el bucle)
    //  - es la propia llamada de login o de refresh
    //  - es una sesion limitada de WhatsApp: no tiene refresh token, por diseño
    // Las rutas de recuperación de contraseña también son públicas: un 400/429 no
    // es de sesión y un 401 aquí jamás debe disparar renovación ni redirección.
    const esRutaDeSesion =
      original?.url?.includes('/auth/refresh') ||
      original?.url?.includes('/auth/login') ||
      original?.url?.includes('/auth/forgot-password') ||
      original?.url?.includes('/auth/reset-password');
    const esSesionLimitada = localStorage.getItem('whatsappScoped') === 'true';

    if (!es401 || !original || original._reintentada || esRutaDeSesion || esSesionLimitada) {
      if (es401 && !esRutaDeSesion) irALogin();
      return Promise.reject(error);
    }

    try {
      const tokenNuevo = await renovarSesion();
      original._reintentada = true;
      original.headers = { ...original.headers, Authorization: `Bearer ${tokenNuevo}` };
      return api(original);
    } catch (fallo) {
      console.warn('🔒 No se pudo renovar la sesion:', fallo.message);
      irALogin();
      return Promise.reject(error);
    }
  }
);

export default api;
